import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHead } from "@/components/PageHead";
import { useQuery } from "@tanstack/react-query";

import { assetData, type AssetDataRecord, type AssetDataSignal, type AssetDataVal, type AssetDataLatest } from "@/api/client";
import { useCursorTail } from "@/lib/hooks/useCursorTail";
import { useSticky } from "@/lib/hooks/useSticky";
import { ago, POLL } from "@/lib/format";
import { ContextStrip } from "@/modules/asset_data/ContextStrip";
import { DevicePicker } from "@/modules/asset_data/picker";
import { RecordTable, fmt } from "@/modules/asset_data/RecordTable";
import { Gauge, BalanceBar } from "@/modules/asset_data/Gauge";
import { KV } from "@/components/KV";

import { Icon } from "@/components/icons";
// Hard cap on rows held in the browser for the live tail. Bounds RAM on a tab
// left open for hours: at the 5–15 s snapshot cadence this is ~15–30 min of
// scrollback, and the buffer NEVER grows past it (oldest rows drop off).
const MAX_ROWS = 150;

// ---- tiny presentational helpers -------------------------------------------

/** A titled card with a coloured accent stripe — the section container. */
function Section({ title, accent, right, children }: {
  title: string; accent: string; right?: ReactNode; children: ReactNode;
}) {
  return (
    <div className="panel" style={{ borderTop: `3px solid ${accent}`, marginBottom: 14, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "13px 18px 0" }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: accent }} />
        <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: "var(--t-bright)" }}>{title}</span>
        {right && <span style={{ marginLeft: "auto" }}>{right}</span>}
      </div>
      <div style={{ padding: "12px 18px 16px" }}>{children}</div>
    </div>
  );
}


/** Compact stat tile: value on top, small label under, coloured accent underline. */
function Stat({ label, value, unit, accent }: { label: string; value: ReactNode; unit?: string; accent: string }) {
  return (
    <div style={{ padding: "8px 0", borderBottom: `2px solid color-mix(in srgb, ${accent} 45%, transparent)` }}>
      <div className="mono" style={{ fontSize: 19, fontWeight: 700, color: "var(--t-bright)", lineHeight: 1.1 }}>
        {value}{unit && <span style={{ fontSize: 11, color: "var(--t-dim)", fontWeight: 400 }}> {unit}</span>}
      </div>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--t-mute)", marginTop: 2 }}>{label}</div>
    </div>
  );
}

/** Red/green pill for a bitfield fault register. 0 = OK (green), non-0 = fault
 *  (red). `hint` is the plain-English meaning, shown on hover. */
function FaultChip({ label, bits, hint }: { label: string; bits: number | null | undefined; hint: string }) {
  const ok = bits == null || bits === 0;
  const col = ok ? "var(--ok)" : "var(--bad)";
  const title = `${hint} - ${ok ? "OK, no bits set" : `FAULT 0x${(bits as number).toString(16)}`}`;
  return (
    <span className="chip" title={title} style={{ color: col, background: `color-mix(in srgb, ${col} 13%, transparent)`, fontSize: 10.5, cursor: "help" }}>
      {ok ? "✓" : "⚠"} {label}{ok ? "" : ` 0x${(bits as number).toString(16)}`}
    </span>
  );
}

/** Neutral info pill with a hover explanation (cursor: help). */
function InfoChip({ children, hint }: { children: ReactNode; hint: string }) {
  return (
    <span className="chip" title={hint} style={{ fontSize: 10.5, color: "var(--t-mute)", background: "var(--well)", cursor: "help" }}>
      {children}
    </span>
  );
}

const grid = (min = 150): CSSProperties => ({ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: "0 20px" });

// ---- value access + scaling -------------------------------------------------

type Vals = Record<string, AssetDataVal>;
function num(v: Vals, k: string): number | null {
  const x = v[k];
  return typeof x === "number" ? x : null;
}
function str(v: Vals, k: string): string | null {
  const x = v[k];
  return x == null || x === "" ? null : String(x);
}
const dash = (x: ReactNode) => (x == null || x === "" ? <span style={{ color: "var(--t-dim)" }}>—</span> : x);
// deci-unit → real unit (temps, alt, hdop, gps speed are all ×10 on the wire)
const deci = (v: Vals, k: string) => { const n = num(v, k); return n == null ? null : n / 10; };

const FIX_LABEL: Record<number, string> = { 0: "no fix", 2: "2D fix", 3: "3D fix" };

// ============================================================================
//  DASHBOARD  — Device / Battery / Motor sections, then the record table
// ============================================================================

function Dashboard({ imei }: { imei: string }) {
  const q = useQuery({ queryKey: ["live", "latest", imei], queryFn: () => assetData.latest(imei), refetchInterval: POLL.STATE });
  const l = q.data?.latest as AssetDataLatest | undefined;
  if (!l) return <div className="panel panel-pad cap">No snapshot for this device yet.</div>;
  const v = l.values;

  // LOCATION, FROM THE SERVER'S `position` BLOCK — not from `values`.
  //
  // `values.lat/lng` is the PACKET as decoded, and it is correctly NULL when
  // the device reported no fix (D-07). Reading it here is what made the card
  // say "No GPS fix yet — location unavailable" for a bike that had been
  // parked in the same spot for an hour: true about the packet, useless about
  // the asset. `position` answers the other question — where it was last, and
  // how long ago — and says which of the two it is handing back.
  const pos = l.position;
  const hasGps = pos?.ever && pos.lat != null && pos.lng != null;
  const liveFix = !!(hasGps && pos.fix);
  const mapUrl = hasGps ? `https://www.google.com/maps/search/?api=1&query=${pos.lat},${pos.lng}` : null;
  const posAccent = liveFix ? "var(--info)" : "var(--warn)";
  const fix = num(v, "gps_fix") ?? 0;

  const packI = num(v, "pack_i");
  const charging = packI != null && packI < 0;

  return (
    <>
      {/* status strip */}
      <div className="panel panel-pad" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <span className="mono" style={{ color: "var(--t-bright)", fontWeight: 700, fontSize: 14 }}>{l.imei}</span>
        <span className="chip" style={{
          color: l.online ? "var(--ok)" : "var(--warn)",
          background: `color-mix(in srgb, ${l.online ? "var(--ok)" : "var(--warn)"} 13%, transparent)`,
        }}>{l.online ? "● ONLINE" : "○ STALE"}</span>
        {l.fw_ver && <span className="chip" style={{ color: "var(--info)", background: "color-mix(in srgb, var(--info) 12%, transparent)" }}>app {l.fw_ver}</span>}
        <ContextStrip imei={l.imei} />
        {l.company_id && <span style={{ fontSize: 11.5, color: "var(--t-mute)" }}>· {l.company_id === "__inventory__" ? "inventory" : l.company_id}</span>}
        <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--t-mute)" }}>
          {/* TWO CLOCKS, never collapsed into one: the DEVICE spoke `seen` ago,
              the POSITION was true `GPS` ago. A bike parked indoors is online
              with an hour-old fix, and one number cannot say which it is. */}
          seq {l.seq ?? "—"} · seen {ago(l.last_seen)} ago
          {pos?.at ? <> · GPS {ago(pos.at)} ago{!pos.fix ? " (last known)" : ""}</> : null}
        </span>
      </div>

      {/* ---------------- DEVICE INFO ---------------- */}
      <Section title="Device Info" accent="var(--info)">
        <div style={{ display: "grid", gridTemplateColumns: "minmax(240px, 1.1fr) 2fr", gap: 20, alignItems: "start" }}>
          {/* location card → Google Maps */}
          {mapUrl ? (
            <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="hovercard"
              style={{ textDecoration: "none", display: "block", border: `1px solid ${liveFix ? "var(--line2)" : "color-mix(in srgb, var(--warn) 55%, transparent)"}`, borderRadius: 14, padding: 14, background: `color-mix(in srgb, ${posAccent} 7%, transparent)` }}
              title="Open this position in Google Maps">
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: liveFix ? "var(--t-mute)" : "var(--warn)" }}>
                {liveFix ? "📍 Live location" : "📍 Last known location"}
              </div>
              <div className="mono" style={{ fontSize: 20, fontWeight: 800, color: "var(--t-bright)", margin: "6px 0 2px" }}>{pos.lat!.toFixed(5)}, {pos.lng!.toFixed(5)}</div>
              {/* THE STALENESS INDICATOR. A position with no age beside it
                  reads as "now", and that is the whole failure this card had:
                  a coordinate on screen is taken as current unless something
                  says otherwise. */}
              <div style={{ fontSize: 11.5, fontWeight: liveFix ? 400 : 700, color: liveFix ? "var(--t-mute)" : "var(--warn)" }}>
                {liveFix
                  ? <>fix as of {ago(pos.at)} ago</>
                  : <>no fix right now - this is where it was {ago(pos.at)} ago</>}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--t-mute)" }}>
                {FIX_LABEL[fix] ?? "—"} · {fmt(num(v, "gps_sat") ?? 0)} sats · HDOP {fmt(deci(v, "gps_hdop_d") ?? "—")}
                {deci(v, "gps_alt_dm") != null ? ` · alt ${fmt(deci(v, "gps_alt_dm"))} m` : ""}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: posAccent, marginTop: 9 }}>Open in Google Maps ↗</div>
            </a>
          ) : (
            <div style={{ border: "1px solid var(--line2)", borderRadius: 14, padding: 14, color: "var(--t-dim)", fontSize: 12.5 }}>
              📍 No GPS fix has EVER been reported for this device - there is no
              last known position to show.
            </div>
          )}
          {/* identity list */}
          <div style={grid(220)}>
            <KV k="IMEI" v={l.imei} accent="var(--info)" />
            <KV k="Board ID" v={dash(l.board_id)} accent="var(--info)" />
            <KV k="Cluster ID" v={dash(str(v, "cluster_id"))} accent="var(--info)" />
            <KV k="Vehicle ID" v={dash(str(v, "vehicle_id"))} accent="var(--info)" />
            <KV k="HW ver" v={dash(l.hw_rev ?? (num(v, "hw_version") != null ? String(num(v, "hw_version")) : null))} accent="var(--info)" />
            <KV k="App ver" v={dash(num(v, "app_sw_version") ?? l.fw_ver)} accent="var(--info)" />
            <KV k="Boot ver" v={dash(num(v, "boot_sw_version"))} accent="var(--info)" />
            <KV k="State" v={dash(l.device_state)} accent="var(--info)" mono={false} />
            <KV k="Type" v={dash(l.device_type)} accent="var(--info)" mono={false} />
          </div>
        </div>
      </Section>

      {/* ---------------- BATTERY INFO ---------------- */}
      <Section title="Battery Info" accent="var(--teal)"
        right={<span style={{ fontSize: 11, color: "var(--t-mute)" }}>{charging ? "⚡ charging" : "discharging"}</span>}>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Gauge label="SoC" value={num(v, "soc")} unit="%" color="var(--teal)"
              zones={[{ upTo: 20, color: "var(--bad)" }, { upTo: 50, color: "var(--warn)" }, { upTo: 100, color: "var(--ok)" }]} />
            <Gauge label="SoH" value={num(v, "batt_soh_pct")} unit="%" color="var(--teal)"
              zones={[{ upTo: 60, color: "var(--bad)" }, { upTo: 80, color: "var(--warn)" }, { upTo: 100, color: "var(--ok)" }]} />
          </div>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={grid(120)}>
              <Stat label="Pack V" value={fmt(num(v, "pack_v") ?? "—")} unit="V" accent="var(--teal)" />
              <Stat label="Pack I" value={fmt(num(v, "pack_i") ?? "—")} unit="A" accent={charging ? "var(--ok)" : "var(--teal)"} />
              <Stat label="Batt temp" value={fmt(deci(v, "battery_temp_decic") ?? num(v, "temp_max_c") ?? "—")} unit="°C" accent="var(--warn)" />
              <Stat label="Cycles" value={fmt(num(v, "batt_cycle_count") ?? "—")} accent="var(--teal)" />
              <Stat label="Capacity" value={fmt(num(v, "batt_capacity_mah") ?? "—")} unit="mAh" accent="var(--teal)" />
              <Stat label="Cells" value={fmt(num(v, "cells") ?? "—")} accent="var(--teal)" />
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--t-mute)", marginBottom: 5 }}>
                Cell balance {num(v, "cell_min_idx") != null ? `· weak cell #${num(v, "cell_min_idx")}` : ""}
              </div>
              <BalanceBar minMv={num(v, "cell_min_mv")} maxMv={num(v, "cell_max_mv")} />
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
              <FaultChip label="Batt" bits={num(v, "battery_err_bits")} hint="Battery fault register" />
              <FaultChip label="Alarms" bits={num(v, "batt_alarm_bits")} hint="Battery alarm flags (over-temp, over-current, etc.)" />
              {str(v, "batt_company_id") != null && (
                <span className="chip" title={`Battery vendor / company ID (1 = Battery Smart, 0 = Aphelion)`}
                  style={{ fontSize: 10.5, color: "var(--teal)", background: "color-mix(in srgb, var(--teal) 12%, transparent)", cursor: "help" }}>
                  vendor {str(v, "batt_company_id")}
                </span>
              )}
              {num(v, "batt_load_status") != null && <InfoChip hint="Battery load / charge state reported by the BMS">load {num(v, "batt_load_status")}</InfoChip>}
            </div>
          </div>
        </div>
      </Section>

      {/* ---------------- MOTOR INFO ---------------- */}
      <Section title="Motor Info" accent="var(--warn)">
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Gauge label="Speed" value={num(v, "speed_kmph")} unit="km/h" max={120} color="var(--warn)" />
            <Gauge label="Motor RPM" value={num(v, "motor_rpm")} unit="rpm" max={6000} color="var(--warn)" />
          </div>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={grid(120)}>
              <Stat label="Gear" value={dash(num(v, "gear"))} accent="var(--ok)" />
              <Stat label="Motor temp" value={fmt(deci(v, "motor_temp_decic") ?? "—")} unit="°C" accent="var(--warn)" />
              <Stat label="Ctrl temp" value={fmt(deci(v, "mctrl_temp_decic") ?? "—")} unit="°C" accent="var(--warn)" />
              <Stat label="Odometer" value={num(v, "odometer_m") != null ? (num(v, "odometer_m")! / 1000).toFixed(1) : "—"} unit="km" accent="var(--info)" />
              <Stat label="Trip" value={num(v, "trip_m") != null ? (num(v, "trip_m")! / 1000).toFixed(1) : "—"} unit="km" accent="var(--info)" />
              <Stat label="Supply" value={num(v, "supply_v_mv") != null ? (num(v, "supply_v_mv")! / 1000).toFixed(1) : "—"} unit="V" accent="var(--warn)" />
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
              <FaultChip label="Motor" bits={num(v, "motor_err_bits")} hint="Motor fault register" />
              <FaultChip label="Ctrl" bits={num(v, "mctrl_err_bits")} hint="Motor-controller fault register" />
              <FaultChip label="Throttle" bits={num(v, "throttle_err_bits")} hint="Throttle fault register" />
              {num(v, "status_bits") != null && <InfoChip hint="Vehicle status bitfield (hex) - headlight / indicators / body-IO flags">status 0x{(num(v, "status_bits") as number).toString(16)}</InfoChip>}
              {num(v, "motion_flags") != null && <InfoChip hint="Motion flags (hex) - moving / parked / brake state">motion 0x{(num(v, "motion_flags") as number).toString(16)}</InfoChip>}
            </div>
          </div>
        </div>
      </Section>

    </>
  );
}

/** Live-tailing decoded record table (newest on top) — the tail itself is the
 *  shared useCursorTail hook (single-flight, hidden-guard, dedupe, bounded). */
function LiveTail({ imei }: { imei: string }) {
  const [signals, setSignals] = useState<AssetDataSignal[]>([]);
  const [paused, setPaused] = useState(false);

  const { items: records, clear } = useCursorTail<AssetDataRecord>({
    fetch: async (cursor) => {
      const { records: batch, signals: sig } = await assetData.records(imei, cursor as string | undefined);
      // signals = the static catalog, sent with every response — adopt it ONCE
      // (fresh identity every tick re-rendered the whole table; the render-storm bug)
      if (sig.length) setSignals((prev) => (prev.length ? prev : sig));
      return batch;
    },
    cursorOf: (r) => r.cursor,
    max: MAX_ROWS,
    paused,
    resetKey: imei,
  });

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "14px 0 8px" }}>
        <div className="section-label flush">Decoded records (live tail)</div>
        {paused && <span style={{ color: "var(--warn)", fontWeight: 700, fontSize: 11 }}>❚❚ PAUSED</span>}
        <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--t-mute)" }}>{records.length} rows</span>
        <button className={`btn ${paused ? "teal" : "ghost"}`} onClick={() => setPaused((p) => !p)}>
          <Icon name={paused ? "play" : "pause"} /> {paused ? "Resume" : "Pause"}
        </button>
        <button className="btn ghost" onClick={clear}>Clear</button>
      </div>
      <RecordTable records={records} signals={signals} newestFirst />
    </div>
  );
}

export function AssetDataWindowPage() {
  // Sticky across navigation: leave Live Data and come back → same device.
  const [imei, setImei] = useSticky("live", "imei", "");
  // ...but `?imei=` WINS, once. That is how the fleet map hands a device over:
  // click a dot, land here already watching it. The param is then cleared, so
  // a later pick from the picker is not fought by a stale URL on re-render.
  const [params, setParams] = useSearchParams();
  const deep = params.get("imei");
  useEffect(() => {
    if (!deep) return;
    setImei(deep);
    setParams((prev) => { const p = new URLSearchParams(prev); p.delete("imei"); return p; },
              { replace: true });
  }, [deep]);
  return (
    <div className="mpage on">
      <PageHead eyebrow="Asset Data · Live Window" title="Watch a device"
        desc={<>Pick a company and IMEI to watch its decoded telemetry - device identity, battery and motor dials up top, then a live-tailing record table.</>} />

      <div className="panel panel-pad" style={{ marginBottom: 14 }}>
        <DevicePicker imei={imei} onPick={setImei} />
      </div>

      {imei ? (
        <>
          <Dashboard imei={imei} />
          <LiveTail imei={imei} />
        </>
      ) : (
        <div className="panel panel-pad cap">
          Select a device above to start watching.
        </div>
      )}
    </div>
  );
}
